import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Container,
  Divider
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';
import './Login.css';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });
  const [animateKey, setAnimateKey] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/dashboard');
      }
    });
    return unsubscribe;
  }, [navigate]);

  // Sign in with email/password
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (error) {
      let errorMessage = 'Failed to sign in';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later';
      }
      
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create new account with email/password
  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, {
        displayName: name
      });
      
      // Add user to Firestore database
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: name,
        email: email,
        createdAt: serverTimestamp(),
        role: 'user' // Default role
      });
      
      showAlert('Account created successfully!', 'success');
      navigate('/dashboard');
    } catch (error) {
      let errorMessage = 'Failed to create account';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      }
      
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const handleGoogleSignIn = async () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if this is a new user and add to Firestore if needed
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: user.displayName || 'User',
        email: user.email,
        photoURL: user.photoURL || '',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        role: 'user'
      }, { merge: true }); // merge: true will only update fields that have changed
      
      navigate('/dashboard');
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Send password reset email
  const handleResetPassword = async () => {
    if (!resetEmail) {
      showAlert('Please enter your email address', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetDialogOpen(false);
      showAlert('Password reset email sent! Check your inbox', 'success');
    } catch (error) {
      let errorMessage = 'Failed to send reset email';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      }
      
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Display alert message
  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  // Close alert
  const closeAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Toggle between login and register
  const toggleMode = () => {
    setAnimateKey(prevKey => prevKey + 1);
    setIsLogin(!isLogin);
    setEmail('');
    setPassword('');
    setName('');
  };

  // Login form content
  const LoginForm = (
    <Box 
      className="fade-in"
      sx={{ 
        width: '100%', 
        maxWidth: '100%',
        p: { xs: 2, sm: 3, md: 4 },
        boxSizing: 'border-box'
      }}
    >
      <Typography 
        variant="h4" 
        fontWeight="bold" 
        align="center" 
        sx={{ 
          mb: 3,
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
          wordBreak: 'break-word',
          hyphens: 'auto'
        }}
      >
        Sign in
      </Typography>
      
      <Button
        fullWidth
        variant="outlined"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{ 
          mb: 2,
          py: 1.2,
          borderRadius: '50px',
          border: '1px solid #ddd',
          color: 'text.primary',
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          wordBreak: 'break-word',
          textOverflow: 'ellipsis',
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
          '&:hover': {
            backgroundColor: '#f8f8f8',
            borderColor: '#ddd'
          }
        }}
      >
        Sign in with Google
      </Button>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>or</Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>
      
      <Box component="form" onSubmit={handleSignIn}>
        <TextField
          fullWidth
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          className="animated-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5',
              borderRadius: '50px',
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
        
        <TextField
          fullWidth
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
          className="animated-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5',
              borderRadius: '50px',
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
        
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button 
            onClick={() => setResetDialogOpen(true)}
            disabled={loading}
            sx={{ 
              textTransform: 'none', 
              color: '#ff5252',
              '&:hover': {
                bgcolor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Forgot your password?
          </Button>
        </Box>
        
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            bgcolor: '#ff5252',
            borderRadius: '50px',
            py: 1.5,
            textTransform: 'uppercase',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: '#ff3939',
              transform: 'translateY(-3px)',
              boxShadow: '0 4px 12px rgba(255, 82, 82, 0.3)'
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'SIGN IN'}
        </Button>
      </Box>
    </Box>
  );

  // Register form content
  const RegisterForm = (
    <Box 
      className="fade-in" 
      sx={{ 
        width: '100%', 
        maxWidth: '100%',
        p: { xs: 2, sm: 3, md: 4 },
        boxSizing: 'border-box'
      }}
    >
      <Typography 
        variant="h4" 
        fontWeight="bold" 
        align="center" 
        sx={{ 
          mb: 3,
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
          wordBreak: 'break-word',
          hyphens: 'auto'
        }}
      >
        Create Account
      </Typography>
      
      <Button
        fullWidth
        variant="outlined"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{ 
          mb: 2,
          py: 1.2,
          borderRadius: '50px',
          border: '1px solid #ddd',
          color: 'text.primary',
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          wordBreak: 'break-word',
          textOverflow: 'ellipsis',
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
          '&:hover': {
            backgroundColor: '#f8f8f8',
            borderColor: '#ddd'
          }
        }}
      >
        Sign up with Google
      </Button>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>or</Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>
      
      <Box component="form" onSubmit={handleSignUp}>
        <TextField
          fullWidth
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={loading}
          className="animated-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5',
              borderRadius: '50px',
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
        
        <TextField
          fullWidth
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          className="animated-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5',
              borderRadius: '50px',
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
        
        <TextField
          fullWidth
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
          className="animated-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5',
              borderRadius: '50px',
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
        
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            bgcolor: '#ff5252',
            borderRadius: '50px',
            py: 1.5,
            textTransform: 'uppercase',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: '#ff3939',
              transform: 'translateY(-3px)',
              boxShadow: '0 4px 12px rgba(255, 82, 82, 0.3)'
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'SIGN UP'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container className="auth-container" sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      p: { xs: 1, sm: 2 },
      maxWidth: '100% !important',
      width: '100%'
    }}>
      <Paper 
        className="auth-card"
        elevation={3}
        sx={{ 
          maxWidth: { xs: '100%', sm: '90%', md: '800px' }, 
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          margin: { xs: '8px', sm: '16px' }
        }}
        key={`container-${animateKey}`}
      >
        {isLogin ? (
          // Login Layout (Red on top in mobile, red on left in desktop)
          <>
            <Box
              className={isMobile ? "slide-from-top" : "slide-from-left"}
              key={`colored-${animateKey}`}
              sx={{
                bgcolor: '#ff5252',
                color: 'white',
                width: { xs: '100%', md: '50%' },
                order: { xs: 1, md: 1 },
                p: { xs: 2, sm: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: { xs: 'auto', md: '400px' }
              }}
            >
              <Box className="mobile-text-container">
                <Typography 
                  variant="h4" 
                  component="h1" 
                  fontWeight="bold" 
                  sx={{ 
                    mb: 2,
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2rem' },
                    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    textAlign: 'center'
                  }}
                >
                  Hello, Friend!
                </Typography>
                <Typography 
                  sx={{ 
                    mb: 4, 
                    px: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    textAlign: 'center',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                >
                  Enter your personal details and start journey with us
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                onClick={toggleMode}
                disabled={loading}
                className="toggle-button"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  borderRadius: '50px',
                  px: 4,
                  py: 1,
                  textTransform: 'uppercase',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                SIGN UP
              </Button>
            </Box>
            <Box
              key={`form-${animateKey}`}
              sx={{
                width: { xs: '100%', md: '50%' },
                order: { xs: 2, md: 2 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                p: { xs: 1, sm: 2, md: 0 }
              }}
            >
              <Box className="form-container" sx={{ 
                width: '100%', 
                maxWidth: '100%',
                p: { xs: 2, sm: 3, md: 4 },
                boxSizing: 'border-box'
              }}>
                {LoginForm}
              </Box>
            </Box>
          </>
        ) : (
          // Register Layout (Form on left, red on right in desktop)
          <>
            <Box
              key={`form-${animateKey}`}
              sx={{
                width: { xs: '100%', md: '50%' },
                order: { xs: 2, md: 1 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden'
              }}
            >
              <Box className="form-container" sx={{ 
                width: '100%', 
                maxWidth: '100%',
                p: { xs: 2, sm: 3, md: 4 },
                boxSizing: 'border-box'
              }}>
                {RegisterForm}
              </Box>
            </Box>
            <Box
              className={isMobile ? "slide-from-bottom" : "slide-from-right"}
              key={`colored-${animateKey}`}
              sx={{
                bgcolor: '#ff5252',
                color: 'white',
                width: { xs: '100%', md: '50%' },
                order: { xs: 1, md: 2 },
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <Box className="mobile-text-container">
                <Typography 
                  variant="h4" 
                  component="h1" 
                  fontWeight="bold" 
                  sx={{ 
                    mb: 2,
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2rem' },
                    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    textAlign: 'center'
                  }}
                >
                  Welcome Back!
                </Typography>
                <Typography 
                  sx={{ 
                    mb: 4, 
                    px: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    textAlign: 'center',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                >
                  To keep connected with us please login with your personal info
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                onClick={toggleMode}
                disabled={loading}
                className="toggle-button"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  borderRadius: '50px',
                  px: 4,
                  py: 1,
                  textTransform: 'uppercase',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                SIGN IN
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Password Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !loading && setResetDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your email address. We'll send you a link to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={loading}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResetDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#ff5252',
              '&:hover': {
                bgcolor: '#ff3939'
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeAlert} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}